import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import userAdminService from '../../services/userAdminService';
import emailOutboxService from '../../services/emailOutboxService';
import type { PlanoTrabalho } from '../../types/planoTrabalho.types';
import { DIAS_PLANO_PARADO, planosParados } from '../../utils/planoParado';
import WorkspaceSection from './WorkspaceSection';
import Button from '../ui/Button';

interface Props {
  planos: PlanoTrabalho[];
  collapsed: boolean;
  onToggleCollapse: () => void;
}

type Item = { chave: string; texto: string; detalhe?: string; acao: string; rota: string };

const AdminAtencaoSection: React.FC<Props> = ({ planos, collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();

  const usuarios = useQuery({
    queryKey: ['admin-atencao', 'usuarios'],
    queryFn: () => userAdminService.list(),
    staleTime: 60_000,
  });
  const emails = useQuery({
    queryKey: ['admin-atencao', 'emails'],
    queryFn: () => emailOutboxService.list({ status: '', search: '', createdFrom: '', createdTo: '' }, 0, 1),
    staleTime: 60_000,
  });

  const pendentes = (usuarios.data ?? []).filter(u => u.status === 'PENDENTE').length;
  const emailsComFalha = (emails.data?.totals?.EXHAUSTED ?? 0) + (emails.data?.totals?.RETRY ?? 0);
  const parados = planosParados(planos);

  const itens: Item[] = [];
  if (pendentes > 0) {
    itens.push({
      chave: 'usuarios',
      texto: pendentes === 1 ? '1 usuário aguardando aprovação' : `${pendentes} usuários aguardando aprovação`,
      detalhe: 'O cadastro só libera o acesso depois da sua aprovação.',
      acao: 'Ver usuários',
      rota: '/dashboard/usuarios',
    });
  }
  if (emailsComFalha > 0) {
    itens.push({
      chave: 'emails',
      texto: emailsComFalha === 1 ? '1 e-mail com falha de envio' : `${emailsComFalha} e-mails com falha de envio`,
      detalhe: 'Em nova tentativa ou com as tentativas esgotadas.',
      acao: 'Ver e-mails',
      rota: '/dashboard/admin/emails',
    });
  }
  if (parados.length > 0) {
    itens.push({
      chave: 'planos',
      texto: parados.length === 1
        ? `1 plano sem movimentação há ${DIAS_PLANO_PARADO} dias ou mais`
        : `${parados.length} planos sem movimentação há ${DIAS_PLANO_PARADO} dias ou mais`,
      detalhe: parados.slice(0, 3).map(p => p.titulo).join(' · '),
      acao: 'Ver planos',
      rota: '/dashboard/gestao/planos-trabalho',
    });
  }

  const carregando = usuarios.isLoading || emails.isLoading;
  if (carregando) return null;
  const falhou = usuarios.isError && emails.isError;

  return (
    <WorkspaceSection
      color={itens.length > 0 ? 'amber' : 'slate'}
      title="Precisa de atenção"
      count={itens.length > 0 ? itens.length : undefined}
      subtitle={itens.length === 0
        ? (falhou ? 'Não foi possível verificar as pendências agora.' : 'Nada pendente: sem usuários a aprovar, e-mails com falha ou planos parados.')
        : undefined}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
    >
      {itens.length > 0 && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800 dark:border-slate-800">
          {itens.map(item => (
            <li key={item.chave} className="flex items-center justify-between gap-3 px-5 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-fg">{item.texto}</p>
                {item.detalhe && (
                  <p className="truncate text-xs text-fg-subtle">{item.detalhe}</p>
                )}
              </div>
              <Button variant="secondary" size="sm" className="shrink-0" onClick={() => navigate(item.rota)}>
                {item.acao}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </WorkspaceSection>
  );
};

export default AdminAtencaoSection;
